import { DomainError } from './errors';

/**
 * LOS HECHOS DEL PROTOCOLO DE SINCRONIZACIÓN (PRD 6.3).
 *
 * Qué colecciones hay, qué mutación escribe en cuál, cuánto dura una lápida.
 * Viven en `domain` y no en `contracts` porque los necesitan los tres lados: el
 * cliente para encolar, `contracts` para construir los esquemas de validación, y
 * `db` para decidir en qué tabla escribe cada mutación.
 *
 * La puerta de dependencias del PRD 4.4 impide que `db` importe `contracts`, y
 * con razón: si lo hiciera, la capa de datos dependería del formato del cable, y
 * cambiar el cable obligaría a tocar el SQL. Así, lo que comparten los tres es
 * justo lo que es verdad en los tres: los nombres.
 */

export const COLECCIONES = [
  'life_area',
  'identity_statement',
  'goal',
  'goal_milestone',
  'habit',
  'habit_entry',
  'focus_session',
  'journal_entry',
  'mood_log',
  'review_session',
  'review_action',
  'task',
  'day_plan',
  'week_plan',
  'life_wheel',
  'dream',
  'dream_step',
  'progression_ledger',
  'achievement_unlock',
  'sealed_season',
  'external_event',
] as const;

export type Coleccion = (typeof COLECCIONES)[number];

/**
 * CLIENTES DE DOS VERSIONES A LA VEZ.
 *
 * La PWA se actualiza cuando el usuario acepta el aviso, así que tras un despliegue
 * un dispositivo puede seguir días con el cliente anterior. Si el pull le manda
 * documentos de una colección que no conoce, no tiene tabla donde ponerlos, la
 * transacción se aborta, el checkpoint no avanza y deja de recibir todo.
 *
 * Por eso el cliente declara qué colecciones entiende y el servidor sirve solo
 * esas. Quien no declara nada es un cliente de antes de la migración 0008, y
 * recibe las quince de entonces. Esta lista NO crece: es lo que entiende un
 * cliente que ya está desplegado y no se puede cambiar.
 */
export const COLECCIONES_LEGADO: readonly Coleccion[] = [
  'life_area',
  'identity_statement',
  'goal',
  'goal_milestone',
  'habit',
  'habit_entry',
  'focus_session',
  'journal_entry',
  'mood_log',
  'review_session',
  'review_action',
  'progression_ledger',
  'achievement_unlock',
  'sealed_season',
  'external_event',
];

function esColeccionConocida(valor: string): valor is Coleccion {
  return (COLECCIONES as readonly string[]).includes(valor);
}

/** El parámetro `colecciones` del pull: orden estable, para que la URL no varíe. */
export function serializarColecciones(colecciones: readonly string[]): string {
  return [...new Set(colecciones)].sort().join(',');
}

/**
 * Lo que el servidor sirve a un cliente. Sin declaración, el legado. Los nombres
 * que el servidor no conoce se ignoran: un cliente más nuevo que el servidor (el
 * despliegue nunca es atómico del todo) no debe recibir un error por eso.
 */
export function coleccionesDeclaradas(raw: string | undefined | null): readonly Coleccion[] {
  const nombres = (raw ?? '')
    .split(',')
    .map((n) => n.trim())
    .filter((n) => n.length > 0);
  if (nombres.length === 0) return COLECCIONES_LEGADO;
  return [...new Set(nombres)].filter(esColeccionConocida);
}

/**
 * ¿Declara ahora el cliente alguna colección que antes no declaró? Entonces el
 * servidor le filtró sus documentos mientras tanto, y su checkpoint ya pasó por
 * encima: solo una descarga completa los trae. `null` = el cliente no guardó
 * nada, es decir, venía de la versión que no declaraba y recibía el legado.
 */
export function faltanColecciones(
  guardadas: readonly string[] | null,
  declaradas: readonly string[],
): boolean {
  const antes = new Set<string>(guardadas ?? COLECCIONES_LEGADO);
  return declaradas.some((c) => !antes.has(c));
}

/**
 * Las que el cliente **no** escribe nunca: las calcula el servidor (el ledger,
 * los logros) o las importa de un proveedor externo (el calendario).
 *
 * Un push que intente tocarlas se rechaza con `forbidden`, no se ignora: un
 * rechazo silencioso es un cliente que lo seguirá intentando para siempre y una
 * cola que no se vacía nunca.
 */
export const COLECCIONES_SOLO_SERVIDOR: readonly Coleccion[] = [
  'progression_ledger',
  'achievement_unlock',
  'external_event',
];

/**
 * Cada tipo de mutación escribe en **una** colección.
 *
 * La regla que los gobierna: **nunca se sincroniza un interruptor, se sincroniza
 * un estado absoluto** (PRD 6.5). `habit.setStatus` es idempotente y conmutativo;
 * un `habit.toggle` no sería ninguna de las dos cosas, porque aplicarlo dos veces
 * deshace lo que hizo. La interfaz puede seguir siendo un interruptor —y debe
 * serlo—; la mutación que viaja, jamás.
 */
export const TIPOS_MUTACION = {
  'area.upsert': 'life_area',
  'identity.upsert': 'identity_statement',
  'goal.upsert': 'goal',
  'goalMilestone.upsert': 'goal_milestone',
  'habit.upsert': 'habit',
  'habit.setStatus': 'habit_entry',
  'focus.record': 'focus_session',
  'journal.save': 'journal_entry',
  'mood.record': 'mood_log',
  'review.save': 'review_session',
  'reviewAction.save': 'review_action',
  'task.upsert': 'task',
  'dayPlan.save': 'day_plan',
  'weekPlan.save': 'week_plan',
  'wheel.save': 'life_wheel',
  'dream.upsert': 'dream',
  'dreamStep.upsert': 'dream_step',
  'season.seal': 'sealed_season',
} as const satisfies Record<string, Coleccion>;

export type TipoMutacion = keyof typeof TIPOS_MUTACION;

export const TIPOS_MUTACION_LISTA = Object.keys(TIPOS_MUTACION) as readonly TipoMutacion[];

export function esTipoMutacion(valor: string): valor is TipoMutacion {
  return Object.hasOwn(TIPOS_MUTACION, valor);
}

export function coleccionDe(tipo: TipoMutacion): Coleccion {
  return TIPOS_MUTACION[tipo];
}

// ===========================================================================
// CHECKPOINT
// ===========================================================================

/**
 * El checkpoint es una **tupla**, no un timestamp suelto.
 *
 * Paginar solo por `updated_at` pierde filas en silencio cuando varias comparten
 * el mismo timestamp — y lo comparten, porque un lote de push las escribe en la
 * misma transacción. El desempate estable por `id` no es un refinamiento: es
 * corrección (PRD 6.3).
 */
export type Checkpoint = { readonly updatedAt: string; readonly id: string };

/** De donde arranca un cliente que no tiene nada. */
export const CHECKPOINT_CERO: Checkpoint = {
  updatedAt: '1970-01-01T00:00:00.000Z',
  id: '00000000-0000-0000-0000-000000000000',
};

export function serializeCheckpoint(cp: Checkpoint): string {
  return `${cp.updatedAt},${cp.id}`;
}

/**
 * Lee un checkpoint del parámetro `since`. Devuelve `null` si no se entiende, y
 * quien llama decide qué hacer.
 *
 * El servidor responde `invalid-request`, **no** lo trata como «desde el
 * principio»: un checkpoint ilegible interpretado como cero descarga el historial
 * entero cada vez que el cliente se equivoque en una coma, y eso en un móvil con
 * datos es una factura.
 */
export function parseCheckpoint(raw: string | undefined | null): Checkpoint | null {
  if (!raw) return null;
  const corte = raw.lastIndexOf(',');
  if (corte <= 0) return null;
  const updatedAt = raw.slice(0, corte);
  const id = raw.slice(corte + 1);
  if (!updatedAt || !id) return null;
  if (Number.isNaN(Date.parse(updatedAt))) return null;
  return { updatedAt, id };
}

// ===========================================================================
// LÍMITES
// ===========================================================================

/** Máximo por lote (PRD 12.5). Más que esto y el cuerpo pasa de 1 MB. */
export const MAX_MUTACIONES_POR_LOTE = 500;
export const LIMITE_PULL_DEFECTO = 500;
export const LIMITE_PULL_MAXIMO = 1000;

/**
 * Días que se conservan las lápidas (PRD 6.10).
 *
 * Un cliente que lleve más que esto sin sincronizar recibe `checkpoint-too-old` y
 * hace una descarga limpia. Es correcto y es raro. Sin lápidas, ese cliente
 * resucitaría al volver todo lo que se borró mientras estaba fuera —y resucitar
 * un hábito abandonado hace cuatro meses es peor que pedirle que descargue.
 */
export const DIAS_LAPIDA = 90;

/** Cada 25 s: por debajo de los 30 con que muchos proxies cortan por inactividad. */
export const LATIDO_MS = 25_000;

/**
 * Si un checkpoint es tan viejo que sus lápidas ya se purgaron.
 *
 * El margen de un día es a propósito: la purga corre de madrugada, así que un
 * checkpoint de hace exactamente 90 días puede haber perdido sus lápidas hace
 * horas o no haberlas perdido aún. Se prefiere mandar a re-bootstrap un cliente
 * que tal vez no lo necesitaba antes que dejarle resucitar filas borradas.
 */
export function checkpointCaducado(cp: Checkpoint, ahoraMs: number): boolean {
  const ms = Date.parse(cp.updatedAt);
  if (Number.isNaN(ms)) throw new DomainError('invalid-request', 'checkpoint ilegible');
  if (ms === Date.parse(CHECKPOINT_CERO.updatedAt)) return false;
  return ahoraMs - ms > (DIAS_LAPIDA - 1) * 86_400_000;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS COLUMNAS DEL CABLE, POR COLECCIÓN.
 *
 * Esto existe por un bug concreto: el cliente llamaba `target` a un campo que en el
 * servidor era `target_per_period`, y **cada `habit.upsert` se rechazaba entero** con
 * «la columna target no existe en habit». No dio error de compilación ni de test:
 * apareció usando la app, con 185 cambios retirados de la cola.
 *
 * La lista está aquí, en el único paquete que ven los dos lados, y la sujetan dos
 * cosas:
 *
 *  · Un test en `apps/api` compara esta lista con el registro que se deriva del
 *    esquema de Drizzle. Si alguien renombra una columna, **falla**.
 *  · El cliente comprueba en desarrollo que toda propiedad que encola está aquí. Si
 *    alguien añade un campo al almacén local y olvida el servidor, **falla al
 *    escribirlo**, no tres semanas después en la cola de un usuario.
 *
 * No es el esquema Zod por mutación que pedía el PRD 12.2 —eso es más— pero cierra
 * exactamente el agujero por el que se coló este fallo, que es por donde se cuelan.
 *
 * Y vive en `domain` y no en `contracts` por una razón medida: `contracts` importa Zod,
 * y el cliente solo necesitaba esta lista. Traerla desde allí metió **Zod entero en el
 * bundle inicial** —de 169 a 196 kB gzip, con el techo en 180— y lo cazó el presupuesto
 * en `pnpm verify`. Aquí son nombres, que es lo que son.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const PROPS_POR_COLECCION: Record<string, readonly string[]> = {
  life_area: [
    'id',
    'name',
    'marca',
    'colorToken',
    'sortOrder',
    'archivedAt',
    'wheelKey',
    'deletedAt',
  ],
  identity_statement: ['id', 'areaId', 'text', 'adoptedAt', 'retiredAt', 'deletedAt'],
  goal: [
    'id',
    'areaId',
    'identityId',
    'title',
    'horizon',
    'targetDate',
    'status',
    'completedAt',
    'localDate',
    'createdAt',
    'deletedAt',
  ],
  goal_milestone: ['id', 'goalId', 'title', 'doneAt', 'localDate', 'sortOrder', 'deletedAt'],
  habit: [
    'id',
    'areaId',
    'identityId',
    'goalId',
    'name',
    'marca',
    'description',
    'extraAreaIds',
    'cadence',
    'target',
    'unit',
    'reminderTime',
    'sortOrder',
    'archivedAt',
    'createdAt',
    'deletedAt',
  ],
  habit_entry: [
    'id',
    'habitId',
    'localDate',
    'status',
    'value',
    'tz',
    'clientTs',
    'attested',
    'source',
    'externalEventId',
    'deletedAt',
  ],
  focus_session: [
    'id',
    'habitId',
    'areaId',
    'goalId',
    'label',
    'localDate',
    'startedAt',
    'endedAt',
    'plannedMinutes',
    'actualMinutes',
    'interrupted',
    'supersedesId',
    'tz',
    'attested',
    'deletedAt',
  ],
  journal_entry: [
    'id',
    'localDate',
    'bodyCt',
    'iv',
    'keyVersion',
    'privateMode',
    'tags',
    'tz',
    'deletedAt',
  ],
  mood_log: ['id', 'localDate', 'mood', 'energy', 'note', 'notedAt', 'tz', 'attested', 'deletedAt'],
  review_session: [
    'id',
    'periodType',
    'periodKey',
    'answers',
    'localDate',
    'completedAt',
    'attested',
    'deletedAt',
  ],
  review_action: ['id', 'reviewId', 'text', 'doneAt', 'deletedAt'],
  task: [
    'id',
    'title',
    'plannedDate',
    'areaId',
    'goalId',
    'doneAt',
    'doneLocalDate',
    'sortOrder',
    'tz',
    'attested',
    'createdAt',
    'deletedAt',
  ],
  day_plan: [
    'id',
    'localDate',
    'mission',
    'pillarAreaId',
    'frogTaskId',
    'projectGoalIds',
    'ritualSealedAt',
    'tz',
    'attested',
    'deletedAt',
  ],
  week_plan: ['id', 'weekKey', 'reward', 'deletedAt'],
  life_wheel: ['id', 'monthKey', 'scores', 'sealedAt', 'localDate', 'tz', 'attested', 'deletedAt'],
  dream: [
    'id',
    'areaId',
    'title',
    'why',
    'estimatedMonths',
    'startedOn',
    'status',
    'trophyForm',
    'trophyMeaning',
    'completedAt',
    'localDate',
    'tz',
    'createdAt',
    'deletedAt',
  ],
  dream_step: [
    'id',
    'dreamId',
    'title',
    'dueOn',
    'doneAt',
    'sortOrder',
    'suggestedBy',
    'deletedAt',
  ],
  sealed_season: [
    'id',
    'seasonKey',
    'label',
    'fromDate',
    'toDate',
    'points',
    'ligaKey',
    'ligaName',
    'milestones',
    'sealedAt',
  ],
  progression_ledger: [
    'id',
    'rulesetVersion',
    'totalPoints',
    'dimensions',
    'level',
    'levelFloor',
    'computedAt',
    'deletedAt',
  ],
  achievement_unlock: ['id', 'achievementKey', 'n', 'name', 'unlockedAt', 'localDate', 'attested'],
  external_event: [
    'id',
    'provider',
    'externalId',
    'kind',
    'title',
    'calendarLabel',
    'startsAt',
    'endsAt',
    'localDate',
    'allDay',
    'tz',
    'raw',
    'importedAt',
    'deletedAt',
  ],
};

/**
 * `body` es el caso especial: el cliente manda el diario en claro y la API lo cifra en
 * `bodyCt`/`iv` antes de guardarlo (PRD 16.4), así que viaja una propiedad que no es
 * columna. Se declara aquí para que la comprobación del cliente no la rechace.
 */
export const PROPS_SOLO_DEL_CABLE: Record<string, readonly string[]> = {
  journal_entry: ['body'],
};
